import React, { useMemo, useRef, useState } from 'react'
import { useTranslation } from 'react-i18next'
import {
  PaymentMethodModalCountryAndState,
  PaymentMethodModalCountryAndStateError,
  PaymentMethodModalStyled,
} from './PaymentMethodModal.styled'
import {
  Modal,
  Button,
  useFrontEndEventTrackingService,
  GeneralSelect,
} from '@clickcease/cc-client-components-lib'
import { StripeWrapper } from 'Features/checkout/components/StripeForm/StripeForm'
import { isMobile } from 'react-device-detect'
import { useUpdatePaymentMethod } from '../../../api/updatePaymentMethod'
import { useAppStore } from 'Stores/appStoreModuleFed'
import { useUpdateAccountSettings } from 'Features/accountSettings/api/updateAccountSettings'
import { useGetPlansDetails } from 'Features/accountSettings/api/getPlansDetails'
import { Countries } from 'Assets/lists/countries'
import { SelectOption } from 'Types/SelectOption'
import UsStates from 'Assets/lists/usStates'
import { useGetAccountSettings } from 'Features/accountSettings/api/getBilling'
import { useUserStore } from 'Stores/userStoreModuleFed'

interface PaymentMethodModalProps {
  isOpen?: boolean
  onOpenChange: (a: boolean) => void
  origin?: string
  openPaymentMethod?: boolean
}

type Form = {
  country: string
  state: string
}

export const PaymentMethodModal = ({
  isOpen,
  onOpenChange,
  origin,
  openPaymentMethod,
}: PaymentMethodModalProps) => {
  const { t } = useTranslation('accountSettings')
  const submitRef = useRef<HTMLButtonElement>()
  const updatePaymentMethod = useUpdatePaymentMethod()
  const [isLoading, setIsLoading] = useState<boolean>(false)
  const { showToaster } = useAppStore()
  const { track } = useFrontEndEventTrackingService()
  const { mutate } = useUpdateAccountSettings()
  const { data: planDetails } = useGetPlansDetails()
  const [accountSettings] = useGetAccountSettings()
  const { accountName } = useUserStore()

  const accountData = useMemo(() => {
    if (!(accountSettings.data instanceof Error)) {
      return accountSettings.data
    }
  }, [accountSettings])

  const [formError, setFormError] = useState<string>('')
  const shouldEnterCountryField = planDetails?.noCreditCard

  const [form, setForm] = useState<Form>({
    country: accountData?.country || '',
    state: accountData?.state || '',
  })

  const sendSegmentEvent = () => {
    if (openPaymentMethod) {
      let text = ''
      switch (origin) {
        case 'trialEndedBanner':
          text = 'Trial ended top bar card inserted'
          break
        case 'trialEndedModal':
          text = 'Trial ended modal card inserted'
          break
        case 'trialPreEndModal':
          text = 'Trial pre end modal card inserted'
          break
        case 'modifyPlan':
          text = 'Modify plan card inserted'
          break
      }
      track(['segment'], text, {})
    }
  }

  const setSelectedField = (type: string, value: string) => {
    setForm({
      ...form,
      [type]: value,
    })
  }

  const updateCountry = () => {
    mutate(
      { accountName, country: form.country, state: form.state },
      {
        onSuccess: () => {
          onEditSuccess()
        },
      }
    )
  }

  const validateCountryState = () => {
    if (form.country.length === 0) {
      setFormError(t('edit_billing_details_modal.please_select_a_country'))
      setIsLoading(false)
      return false
    } else if (form.country === 'US' && form.state.length === 0) {
      setFormError(t('edit_billing_details_modal.please_select_a_state'))
      setIsLoading(false)
      return false
    } else {
      setFormError('')
      return true
    }
  }

  const onEditSuccess = () => {
    sendSegmentEvent()
    setIsLoading(false)
    showToaster(t('edit_payment_method_modal.credit_card_updated'), 'success')
    setTimeout(() => {
      onOpenChange(false)
    }, 1000)
  }

  const stripeSuccess = (result: string) => {
    if (validateCountryState()) {
      updatePaymentMethod.mutate(result, {
        onSuccess: () => {
          if (shouldEnterCountryField) {
            updateCountry()
          } else {
            onEditSuccess()
          }
        },
      })
    }
  }

  const stripeError = (error: string) => {
    setIsLoading(false)
    showToaster(error, 'failure')
  }

  return (
    <Modal
      title={t('edit_payment_method_modal.edit_payment_method')}
      isOpen={isOpen}
      onOpenChange={onOpenChange}
      onCloseClick={() => {}}
      disableAutoFocus
      id={'payment-method-modal'}
    >
      <PaymentMethodModalStyled>
        <StripeWrapper
          onSuccess={stripeSuccess}
          onError={stripeError}
          submitRef={submitRef}
          setIsLoading={(isLoading: boolean) => setIsLoading(isLoading)}
        />
        <PaymentMethodModalCountryAndState>
          <GeneralSelect
            value={Countries.find(
              (item) => item.label === form.country || item.value === form.country
            )}
            options={Countries}
            placeholder={t('edit_billing_details_modal.country')}
            wrapperCSS={{
              width: form.country === 'US' ? '235px' : '100%',
              height: '30px',
              '> div:nth-child(2)': { marginTop: '6px' },
            }}
            onChange={(item: SelectOption) => {
              if (item) setSelectedField('country', item?.value)
            }}
          />
          {form.country === 'US' && (
            <GeneralSelect
              placeholder={t('edit_billing_details_modal.state')}
              value={UsStates.find(
                (item) => item.value === form.state || item.label === form.state
              )}
              wrapperCSS={{
                width: '235px',
                height: '30px',
                '> div:nth-child(2)': { marginTop: '6px' },
              }}
              options={UsStates}
              onChange={(item: SelectOption) => {
                if (item) setSelectedField('state', item?.value)
              }}
            />
          )}
        </PaymentMethodModalCountryAndState>
        {formError.length > 0 && (
          <PaymentMethodModalCountryAndStateError>
            {formError}
          </PaymentMethodModalCountryAndStateError>
        )}
        <Button
          css={{ minWidth: isMobile ? '90%' : '60px', height: '16px', marginTop: '15px' }}
          text={t('edit_payment_method_modal.apply')}
          loading={isLoading}
          disabled={isLoading}
          onClick={() => submitRef.current?.click()}
        />
      </PaymentMethodModalStyled>
    </Modal>
  )
}
